import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSelector } from 'react-redux';
import selectors from 'store/selectors';
import { S3_BUCKET_ASSETS } from 'utils/constants';
import {
  ChatDrawer,
  MainContainer,
  Header,
  HeaderIcon,
  HeaderTitle,
  CloseButton,
  ContentArea,
  PreMessageContainer,
  LoadingContainer,
  LoadingTitle,
  LoadingSubtitle,
  WelcomeContainer,
  WelcomeTitle,
  InputContainer,
  InputWrapper,
  TextArea,
  SendButton,
  SuggestionsTitle,
  SuggestionsContainer,
  SuggestionItem,
  SuggestionText,
  MessagesArea,
  MessageBubble,
  TypingIndicator,
  TypingDots,
  TypingDot,
  TypingText,
  PostMessageInputContainer,
  DisclaimerText,
} from './aichatpanel.style';

interface ChatMessage {
  chatId: string;
  message: string;
  type: 'user' | 'assistant';
  createdAt: Date;
}

interface AIChatPanelProps {
  open: boolean;
  onClose: () => void;
  onSendMessage: (message: string) => Promise<void>;
}

const smartSuggestions = [
  'Show top 3 performing locations this week',
  'Highlight roles with <50% completion rate',
  'Identify locations with consistent improvement over the last 7 days',
];

const AIChatPanel: React.FC<AIChatPanelProps> = ({
  open,
  onClose,
  onSendMessage,
}) => {
  // Get state from Redux
  const chatContextId = useSelector(selectors.getAIChatContextId);
  const chatHistory = useSelector(selectors.getAIChatHistory);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const handleSendMessage = async () => {
    if (!message.trim() || isSending || !chatContextId) return;

    setIsSending(true);
    try {
      await onSendMessage(message.trim());
      setMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setMessage(suggestion);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <MainContainer>
      {/* Header */}
      <Header>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HeaderIcon>
            <img
              src={S3_BUCKET_ASSETS + 'images/stars-01.png'}
              alt="AI Chat"
              style={{
                width: 20,
                height: 20,
                objectFit: 'contain',
              }}
            />
          </HeaderIcon>
          <HeaderTitle variant="subtitle1">Chat with AI</HeaderTitle>
        </Stack>
        <CloseButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </CloseButton>
      </Header>

      {/* Content Area */}
      <ContentArea>
        {chatHistory.length === 0 ? (
          // Pre-message state
          <PreMessageContainer>
            {!chatContextId ? (
              // Loading state
              <LoadingContainer>
                <CircularProgress size={32} sx={{ color: '#6B46C1', mb: 2 }} />
                <LoadingTitle variant="body1">
                  Gathering Context...
                </LoadingTitle>
                <LoadingSubtitle
                  variant="body2"
                  sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
                >
                  Analyzing your report data to provide better assistance
                </LoadingSubtitle>
              </LoadingContainer>
            ) : (
              // Smart suggestions state
              <>
                <WelcomeContainer>
                  <WelcomeTitle variant="h6">How can I help you?</WelcomeTitle>
                </WelcomeContainer>

                {/* Chat Input - Pre-message */}
                <InputContainer>
                  <InputWrapper>
                    <TextArea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask anything"
                      disabled={isSending || !chatContextId}
                    />
                    <SendButton
                      onClick={handleSendMessage}
                      disabled={!message.trim() || isSending || !chatContextId}
                    >
                      {isSending ? (
                        <CircularProgress size={20} sx={{ color: '#6B46C1' }} />
                      ) : (
                        <ArrowUpwardIcon fontSize="small" />
                      )}
                    </SendButton>
                  </InputWrapper>
                </InputContainer>

                {/* Smart Suggestions */}
                <div>
                  <SuggestionsTitle variant="body2">
                    Smart suggestions
                  </SuggestionsTitle>
                  <SuggestionsContainer>
                    {smartSuggestions.map((suggestion, index) => (
                      <SuggestionItem
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        <SuggestionText variant="body2">
                          {suggestion}
                        </SuggestionText>
                        <ChevronRightIcon
                          sx={{
                            color: '#9CA3AF',
                            fontSize: '16px',
                          }}
                        />
                      </SuggestionItem>
                    ))}
                  </SuggestionsContainer>
                </div>
              </>
            )}
          </PreMessageContainer>
        ) : (
          // Post-message state
          <>
            {/* Messages Area */}
            <MessagesArea>
              {chatHistory.map((msg) => (
                <MessageBubble key={msg.chatId} messageType={msg.type}>
                  {msg.message}
                </MessageBubble>
              ))}
              {isSending && (
                <TypingIndicator>
                  <TypingDots>
                    <TypingDot delay="0s" />
                    <TypingDot delay="0.2s" />
                    <TypingDot delay="0.4s" />
                  </TypingDots>
                  <TypingText>Analyzing the report data</TypingText>
                </TypingIndicator>
              )}
              <div ref={messagesEndRef} />
            </MessagesArea>

            {/* Chat Input - Post-message */}
            <PostMessageInputContainer>
              <InputWrapper>
                <TextArea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask anything"
                  disabled={isSending || !chatContextId}
                />
                <SendButton
                  onClick={handleSendMessage}
                  disabled={!message.trim() || isSending || !chatContextId}
                >
                  {isSending ? (
                    <CircularProgress size={20} sx={{ color: '#6B46C1' }} />
                  ) : (
                    <ArrowUpwardIcon fontSize="small" />
                  )}
                </SendButton>
              </InputWrapper>

              {/* Footer disclaimer */}
              <DisclaimerText variant="caption">
                Responses and accuracy may vary. Recheck important information.
              </DisclaimerText>
            </PostMessageInputContainer>
          </>
        )}
      </ContentArea>
    </MainContainer>
  );
};

export default AIChatPanel;
